<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from "vue";
import {
  loadTenantProbeConfiguration,
  publishTenantProbeConfiguration,
  saveTenantProbeConfigurationDraft,
  type ProbeConfiguration,
  type ProbeHttpTargetConfiguration,
  type TenantProbeConfigurationState,
} from "../api/probe-configuration.js";
import type { TenantSummary } from "../api/tenants.js";
import AppShell from "../components/AppShell.vue";
import ProbePageTabs from "../components/ProbePageTabs.vue";
import { formatDateTime } from "../formatting/date-time.js";
import { useTenantStore } from "../stores/tenants.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

interface UrlPreset {
  readonly label: string;
  readonly url: string;
  readonly expectedStatus: number;
}

const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const tenant: ComputedRef<TenantSummary | null> = computed(
  (): TenantSummary | null => tenantStore.selectedTenant,
);
const state: Ref<TenantProbeConfigurationState | null> = ref(null);
const draft: Ref<ProbeConfiguration | null> = ref(null);
const primaryPresetUrl: Ref<string> = ref("");
const secondaryPresetUrls: Ref<string[]> = ref([]);
const loading: Ref<boolean> = ref(false);
const saving: Ref<boolean> = ref(false);
const publishing: Ref<boolean> = ref(false);
const errorMessage: Ref<string | null> = ref(null);
const statusMessage: Ref<string | null> = ref(null);

const canManageConfiguration: ComputedRef<boolean> = computed(
  (): boolean => tenant.value?.access.capabilities.includes("probe.configuration.manage") === true,
);
const urlPresets: ComputedRef<readonly UrlPreset[]> = computed(
  (): readonly UrlPreset[] => [
    {
      label: "Own LacunaScope server",
      url: "/connectivity-check",
      expectedStatus: 204,
    },
    {
      label: "Google gstatic generate_204",
      url: "https://www.gstatic.com/generate_204",
      expectedStatus: 204,
    },
    {
      label: "Google APIs generate_204",
      url: "https://www.googleapis.com/generate_204",
      expectedStatus: 204,
    },
    {
      label: "Microsoft NCSI connecttest",
      url: "http://www.msftconnecttest.com/connecttest.txt",
      expectedStatus: 200,
    },
    {
      label: "Firefox detectportal",
      url: "http://detectportal.firefox.com/success.txt",
      expectedStatus: 200,
    },
  ],
);
const maxCheckCount: ComputedRef<number> = computed(
  (): number => draft.value === null ? 0 : 1 + draft.value.targets.secondary.length,
);
const worstCaseDurationSeconds: ComputedRef<number> = computed(
  (): number => draft.value === null ? 0 : maxCheckCount.value * draft.value.probe.timeoutSeconds,
);
const timingWarning: ComputedRef<string | null> = computed((): string | null => {
  if (draft.value === null) {
    return null;
  }
  if (worstCaseDurationSeconds.value + 1 > draft.value.probe.cycleIntervalSeconds) {
    return `Worst-case target checks can take ${worstCaseDurationSeconds.value}s. The cycle interval must be at least one second longer.`;
  }
  return null;
});
const cycleBoundWarning: ComputedRef<string | null> = computed((): string | null => {
  if (draft.value === null || state.value === null) {
    return null;
  }
  if (draft.value.probe.cycleIntervalSeconds < state.value.bounds.minProbeRequestDelaySeconds) {
    return `Probe cycle interval must be at least ${state.value.bounds.minProbeRequestDelaySeconds}s for this tenant tier.`;
  }
  return null;
});
const uploadBoundWarning: ComputedRef<string | null> = computed((): string | null => {
  if (draft.value === null || state.value === null) {
    return null;
  }
  if (draft.value.upload.intervalSeconds < state.value.bounds.minProbeUploadIntervalSeconds) {
    return `Upload interval must be at least ${state.value.bounds.minProbeUploadIntervalSeconds}s for this tenant tier.`;
  }
  if (draft.value.upload.intervalSeconds < draft.value.probe.cycleIntervalSeconds) {
    return "Upload interval must be at least the probe cycle interval.";
  }
  return null;
});
const secondaryWarning: ComputedRef<string | null> = computed((): string | null => {
  if (draft.value === null || draft.value.targets.secondary.length <= 2) {
    return null;
  }
  return "Use at most 2 secondary targets. More targets can make one scan cycle exceed its interval.";
});
const jitterWarning: ComputedRef<string | null> = computed((): string | null => {
  if (draft.value === null) {
    return null;
  }
  if (draft.value.probe.jitterPercent < 5) {
    return "Jitter below 5% can synchronize many probes and create avoidable request bursts.";
  }
  if (draft.value.probe.jitterPercent > 30) {
    return "Jitter above 30% can make probe timing harder to reason about and may delay expected uploads.";
  }
  return null;
});
const hasBlockingWarnings: ComputedRef<boolean> = computed(
  (): boolean =>
    timingWarning.value !== null ||
    cycleBoundWarning.value !== null ||
    uploadBoundWarning.value !== null ||
    secondaryWarning.value !== null,
);

function cloneConfiguration(configuration: ProbeConfiguration): ProbeConfiguration {
  return JSON.parse(JSON.stringify(configuration)) as ProbeConfiguration;
}

function formatOptionalDateTime(value: string | null): string {
  return value === null ? "—" : formatDateTime(value, userSettingsStore.settings.dateTimeFormat);
}

async function loadConfiguration(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null) {
    state.value = null;
    draft.value = null;
    return;
  }
  loading.value = true;
  errorMessage.value = null;
  statusMessage.value = null;
  try {
    const loadedState: TenantProbeConfigurationState = await loadTenantProbeConfiguration(currentTenant.id);
    state.value = loadedState;
    draft.value = cloneConfiguration(loadedState.draft);
    primaryPresetUrl.value = "";
    secondaryPresetUrls.value = loadedState.draft.targets.secondary.map(
      (_target: ProbeHttpTargetConfiguration): string => "",
    );
  } catch (error: unknown) {
    errorMessage.value = error instanceof Error ? error.message : "Probe configuration could not be loaded.";
  } finally {
    loading.value = false;
  }
}

function addSecondaryTarget(): void {
  if (draft.value === null) {
    return;
  }
  const target: ProbeHttpTargetConfiguration = {
    name: `secondary-${draft.value.targets.secondary.length + 1}`,
    url: "https://example.net/generate-204",
    expectedStatus: 204,
  };
  draft.value = {
    ...draft.value,
    targets: {
      ...draft.value.targets,
      secondary: [...draft.value.targets.secondary, target],
    },
  };
  secondaryPresetUrls.value = [...secondaryPresetUrls.value, ""];
}

function removeSecondaryTarget(index: number): void {
  if (draft.value === null) {
    return;
  }
  draft.value = {
    ...draft.value,
    targets: {
      ...draft.value.targets,
      secondary: draft.value.targets.secondary.filter(
        (_target: ProbeHttpTargetConfiguration, targetIndex: number): boolean => targetIndex !== index,
      ),
    },
  };
  secondaryPresetUrls.value = secondaryPresetUrls.value.filter(
    (_presetUrl: string, presetIndex: number): boolean => presetIndex !== index,
  );
}

function applyTargetPreset(target: ProbeHttpTargetConfiguration, presetUrl: string): void {
  const preset: UrlPreset | undefined = urlPresets.value.find(
    (item: UrlPreset): boolean => item.url === presetUrl,
  );
  if (preset === undefined) {
    return;
  }
  target.url = preset.url;
  target.expectedStatus = preset.expectedStatus;
}

function applyPrimaryPreset(presetUrl: string): void {
  primaryPresetUrl.value = presetUrl;
  if (draft.value !== null) {
    applyTargetPreset(draft.value.targets.primary, presetUrl);
  }
}

function applySecondaryPreset(index: number, presetUrl: string): void {
  secondaryPresetUrls.value[index] = presetUrl;
  const target: ProbeHttpTargetConfiguration | undefined = draft.value?.targets.secondary[index];
  if (target !== undefined) {
    applyTargetPreset(target, presetUrl);
  }
}

async function saveDraft(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || draft.value === null) {
    return;
  }
  saving.value = true;
  errorMessage.value = null;
  statusMessage.value = null;
  try {
    const savedState: TenantProbeConfigurationState = await saveTenantProbeConfigurationDraft(
      currentTenant.id,
      draft.value,
    );
    state.value = savedState;
    draft.value = cloneConfiguration(savedState.draft);
    primaryPresetUrl.value = "";
    secondaryPresetUrls.value = savedState.draft.targets.secondary.map(
      (_target: ProbeHttpTargetConfiguration): string => "",
    );
    statusMessage.value = "Draft saved.";
  } catch (error: unknown) {
    errorMessage.value = error instanceof Error ? error.message : "Draft could not be saved.";
  } finally {
    saving.value = false;
  }
}

async function publishDraft(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null) {
    return;
  }
  publishing.value = true;
  errorMessage.value = null;
  statusMessage.value = null;
  try {
    if (draft.value !== null) {
      await saveTenantProbeConfigurationDraft(currentTenant.id, draft.value);
    }
    const publishedState: TenantProbeConfigurationState = await publishTenantProbeConfiguration(currentTenant.id);
    state.value = publishedState;
    draft.value = cloneConfiguration(publishedState.draft);
    primaryPresetUrl.value = "";
    secondaryPresetUrls.value = publishedState.draft.targets.secondary.map(
      (_target: ProbeHttpTargetConfiguration): string => "",
    );
    statusMessage.value = `Published version ${publishedState.publishedVersion ?? "—"}.`;
  } catch (error: unknown) {
    errorMessage.value = error instanceof Error ? error.message : "Configuration could not be published.";
  } finally {
    publishing.value = false;
  }
}

onMounted(loadConfiguration);
watch((): string | null => tenant.value?.id ?? null, loadConfiguration);
</script>

<template>
  <AppShell v-if="tenant">
    <header class="page-heading">
      <div>
        <p class="eyebrow">{{ tenant.slug }}</p>
        <h1>Probes</h1>
        <p>Probe inventory and tenant-wide configuration delivered to registered probes.</p>
      </div>
      <button class="button button--primary page-heading__action" type="button" :disabled="loading" @click="loadConfiguration">
        Refresh
      </button>
    </header>

    <ProbePageTabs />

    <section class="metric-grid metric-grid--four" aria-label="Probe configuration summary">
      <article class="metric-card">
        <span class="metric-card__label">Published</span>
        <strong>v{{ state?.publishedVersion ?? "—" }}</strong>
        <span>{{ formatOptionalDateTime(state?.publishedAt ?? null) }}</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Draft saved</span>
        <strong>{{ state?.updatedAt === null || state?.updatedAt === undefined ? "No" : "Yes" }}</strong>
        <span>{{ formatOptionalDateTime(state?.updatedAt ?? null) }}</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Min scan</span>
        <strong>{{ state?.bounds.minProbeRequestDelaySeconds ?? "—" }}s</strong>
        <span>Tier-enforced lower bound</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Min upload</span>
        <strong>{{ state?.bounds.minProbeUploadIntervalSeconds ?? "—" }}s</strong>
        <span>Tier-enforced lower bound</span>
      </article>
    </section>

    <section class="content-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Draft</p>
          <h2>Tenant probe configuration</h2>
        </div>
      </div>

      <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
      <p v-if="statusMessage" class="notice">{{ statusMessage }}</p>
      <p v-if="cycleBoundWarning" class="form-error">{{ cycleBoundWarning }}</p>
      <p v-if="timingWarning" class="form-error">{{ timingWarning }}</p>
      <p v-if="uploadBoundWarning" class="form-error">{{ uploadBoundWarning }}</p>
      <p v-if="secondaryWarning" class="form-error">{{ secondaryWarning }}</p>
      <p v-if="loading" class="placeholder-panel">Loading configuration…</p>
      <p v-else-if="!canManageConfiguration" class="placeholder-panel">You do not have permission to edit probe configuration.</p>

      <form v-else-if="draft" class="settings-form" @submit.prevent="saveDraft">
        <label>
          <span>Probe cycle interval · seconds</span>
          <input v-model.number="draft.probe.cycleIntervalSeconds" type="number" :min="state?.bounds.minProbeRequestDelaySeconds ?? 1" step="1" required />
        </label>
        <label>
          <span>Probe timeout · seconds</span>
          <input v-model.number="draft.probe.timeoutSeconds" type="number" min="0.1" max="120" step="0.1" required />
          <small>Worst case: {{ maxCheckCount }} checks × {{ draft.probe.timeoutSeconds }}s = {{ worstCaseDurationSeconds }}s.</small>
        </label>
        <label>
          <span>Jitter · percent</span>
          <input v-model.number="draft.probe.jitterPercent" type="number" min="0" max="100" required />
          <small v-if="jitterWarning" class="field-warning">
            <span aria-hidden="true">⚠</span>
            <span>{{ jitterWarning }}</span>
          </small>
        </label>
        <div class="settings-field--wide">
          <h3>Primary target</h3>
        </div>
        <label>
          <span>Primary URL preset</span>
          <select v-model="primaryPresetUrl" @change="applyPrimaryPreset(($event.target as HTMLSelectElement).value)">
            <option value="">Custom URL</option>
            <option v-for="preset in urlPresets" :key="preset.url" :value="preset.url">
              {{ preset.label }} · expects {{ preset.expectedStatus }}
            </option>
          </select>
        </label>
        <label>
          <span>Primary URL</span>
          <input v-model="draft.targets.primary.url" required maxlength="2048" :readonly="primaryPresetUrl !== ''" />
          <small>{{ primaryPresetUrl === "" ? "Editable. Use a complete URL, including http:// or https://." : "Preset URL. Select Custom URL to edit manually." }}</small>
        </label>
        <label>
          <span>Primary expected status</span>
          <input v-model.number="draft.targets.primary.expectedStatus" type="number" min="100" max="599" required />
        </label>

        <div class="settings-field--wide">
          <div class="content-panel__heading">
            <div>
              <h3>Secondary targets</h3>
              <p>Checked in order when the primary target fails. One successful secondary target proves general connectivity. Maximum: 2.</p>
            </div>
            <button class="button button--quiet" type="button" :disabled="draft.targets.secondary.length >= 2" @click="addSecondaryTarget">Add secondary</button>
          </div>
        </div>
        <div
          v-for="(target, index) in draft.targets.secondary"
          :key="`${target.name ?? 'secondary'}-${index}`"
          class="settings-field--wide content-panel"
        >
          <div class="content-panel__heading">
            <h4>Secondary {{ index + 1 }}</h4>
            <button class="button button--quiet" type="button" @click="removeSecondaryTarget(index)">Remove</button>
          </div>
          <div class="settings-form">
            <label>
              <span>URL preset</span>
              <select
                :value="secondaryPresetUrls[index] ?? ''"
                @change="applySecondaryPreset(index, ($event.target as HTMLSelectElement).value)"
              >
                <option value="">Custom URL</option>
                <option v-for="preset in urlPresets" :key="`${index}-${preset.url}`" :value="preset.url">
                  {{ preset.label }} · expects {{ preset.expectedStatus }}
                </option>
              </select>
            </label>
            <label>
              <span>Name</span>
              <input v-model="target.name" required maxlength="120" />
            </label>
            <label>
              <span>URL</span>
              <input v-model="target.url" required maxlength="2048" :readonly="(secondaryPresetUrls[index] ?? '') !== ''" />
              <small>{{ (secondaryPresetUrls[index] ?? "") === "" ? "Editable. Use a complete URL, including http:// or https://." : "Preset URL. Select Custom URL to edit manually." }}</small>
            </label>
            <label>
              <span>Expected status</span>
              <input v-model.number="target.expectedStatus" type="number" min="100" max="599" required />
            </label>
          </div>
        </div>

        <div class="settings-field--wide">
          <h3>Upload</h3>
        </div>
        <label>
          <span>Upload interval · seconds</span>
          <input v-model.number="draft.upload.intervalSeconds" type="number" :min="state?.bounds.minProbeUploadIntervalSeconds ?? 1" step="1" required />
        </label>
        <label>
          <span>Batch size</span>
          <input v-model.number="draft.upload.batchSize" type="number" min="1" max="16" required />
        </label>
        <label>
          <span>Max batch bytes</span>
          <input v-model.number="draft.upload.maxBatchBytes" type="number" min="1024" max="16384" required />
        </label>

        <div class="settings-actions settings-field--wide">
          <span></span>
          <div>
            <button class="button button--quiet" type="submit" :disabled="saving || publishing || hasBlockingWarnings">
              {{ saving ? "Saving…" : "Save draft" }}
            </button>
            <button class="button button--primary" type="button" :disabled="saving || publishing || hasBlockingWarnings" @click="publishDraft">
              {{ publishing ? "Publishing…" : "Publish" }}
            </button>
          </div>
        </div>
      </form>
    </section>
  </AppShell>
</template>
